import { ReactNode, useState } from 'react'

import CubeIcon from '@heroicons/react/outline/CubeIcon'
import PencilAltIcon from '@heroicons/react/outline/PencilAltIcon'
import UserAddIcon from '@heroicons/react/outline/UserAddIcon'
import ViewGridIcon from '@heroicons/react/outline/ViewGridIcon'
import { Link, useHistory } from 'react-router-dom'

import { hasScopesPermission } from '@tribeplatform/gql-client/lib'
import { useI18n } from '@tribeplatform/react-components/i18n'
import { CreateSpaceFromTemplateModal as CreateSpaceModal } from '@tribeplatform/react-components/Space'
import { useAuthMember, useAuthToken } from '@tribeplatform/react-sdk/hooks'
import { BottomBar } from '@tribeplatform/react-ui-kit/BottomBar'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'
import { Tray } from '@tribeplatform/react-ui-kit/Tray'

import { CollectionFormModal } from '../../Collection/CollectionFormModal'
import { UserImportModal } from '../../UserImport'
import { NavbarControlItem } from '../constants'
import { getIcon } from '../utils'

type CreateMenuItem = {
  name?: string
  href?: string
  icon?: ReactNode
  as?: 'a' | Link
  key?: string
  action?: () => void
}

export type NavbarControlProfileProps = {
  item: NavbarControlItem
  presentation: ReactNode
  mobile?: boolean
  className?: string
}

export const NavbarControlCreateItem = ({
  item,
  presentation,
  mobile = false,
  className,
}: NavbarControlProfileProps) => {
  const intl = useI18n()
  const { data: authToken } = useAuthToken()
  const { isGuest } = useAuthMember()
  const { network } = authToken || {}
  const { push } = useHistory()

  const [trayOpen, setTrayOpen] = useState(false)

  const [openSpaceModal, setOpenSpaceModal] = useState(false)
  const [openCollectionModal, setOpenCollectionModal] = useState(false)
  const [openMemberInviteModal, setOpenMemberInviteModal] = useState(false)

  const [canCreateSpace, canCreateCollection, canInviteMembers] =
    hasScopesPermission(network, [
      'createSpace',
      'createCollection',
      'inviteMembers',
    ])
  const canCreatePost = !isGuest

  if (
    !canCreatePost &&
    !canCreateSpace &&
    !canCreateCollection &&
    !canInviteMembers
  ) {
    return null
  }

  const menuOptions: Array<CreateMenuItem> = []

  if (canCreatePost) {
    menuOptions.push({
      name: intl.$t({
        defaultMessage: 'Add post',
        id: 'Posts.Add',
      }),
      icon: <PencilAltIcon />,
      action: () => push('/new'),
      key: 'add-post',
    })
  }
  if (canCreateSpace) {
    menuOptions.push({
      name: intl.$t({
        defaultMessage: 'Add {SPACE}',
        id: 'Navbar.Controls.AddSpace',
      }),
      icon: <CubeIcon />,
      action: () => setOpenSpaceModal(true),
      key: 'add-space',
    })
  }
  if (canCreateCollection) {
    menuOptions.push({
      name: intl.$t({
        defaultMessage: 'Add {COLLECTION}',
        id: 'Navbar.Controls.AddCollection',
      }),
      icon: <ViewGridIcon />,
      action: () => setOpenCollectionModal(true),
      key: 'add-collection',
    })
  }
  if (canInviteMembers) {
    menuOptions.push({
      name: intl.$t({
        defaultMessage: 'Invite {MEMBERS}',
        id: 'Navbar.Controls.AddMembers',
      }),
      icon: <UserAddIcon />,
      action: () => setOpenMemberInviteModal(true),
      key: 'invite-members',
    })
  }

  const Icon = getIcon(item.icon)

  return (
    <>
      {mobile ? (
        <>
          <BottomBar.Link
            as="button"
            icon={<Icon />}
            onClick={() => setTrayOpen(true)}
          />
          <Tray open={trayOpen} onClose={() => setTrayOpen(false)}>
            <Tray.Header />
            <Tray.Content>
              <Tray.ActionList>
                {menuOptions.map(item => (
                  <Tray.ActionListItem
                    key={item.name}
                    leadingIcon={item.icon as any}
                    onClick={item.action}
                  >
                    {item.name}
                  </Tray.ActionListItem>
                ))}
              </Tray.ActionList>
            </Tray.Content>
          </Tray>
        </>
      ) : (
        <Dropdown placement="bottom-end" className={className}>
          <Dropdown.ButtonMinimal
            className="rounded-full navbar-create-button"
            aria-label={item.label}
          >
            {presentation}
          </Dropdown.ButtonMinimal>
          <Dropdown.Items className="navbar-create-button__dropdown">
            {menuOptions.map(item => {
              if (item?.name === '-') {
                return (
                  <div
                    key="static-key-navbar-divider"
                    className="w-full border-t border-line-subdued my-2"
                  />
                )
              }

              if (item.as === 'a') {
                return (
                  <Dropdown.Item
                    leadingIcon={item.icon as any}
                    key={item.name}
                    as="a"
                    href={item.href || '#'}
                    onClick={item.action}
                  >
                    {item.name}
                  </Dropdown.Item>
                )
              }

              if (item.href) {
                return (
                  <Dropdown.Item
                    leadingIcon={item.icon as any}
                    key={item.name}
                    as={Link}
                    to={item.href}
                    onClick={item.action}
                  >
                    {item.name}
                  </Dropdown.Item>
                )
              }

              return (
                <Dropdown.Item
                  leadingIcon={item.icon as any}
                  key={item.name}
                  onClick={item.action}
                >
                  {item.name}
                </Dropdown.Item>
              )
            })}
          </Dropdown.Items>
        </Dropdown>
      )}

      {openSpaceModal ? (
        <CreateSpaceModal
          open={openSpaceModal}
          onClose={() => {
            setOpenSpaceModal(false)
          }}
        />
      ) : null}
      {openCollectionModal ? (
        <CollectionFormModal
          open={openCollectionModal}
          onClose={() => {
            setOpenCollectionModal(false)
          }}
        />
      ) : null}

      {openMemberInviteModal && (
        <UserImportModal
          open={openMemberInviteModal}
          onClose={() => {
            setOpenMemberInviteModal(false)
          }}
        />
      )}
    </>
  )
}
